/** Screens a signed-in user is never sent back to (a `from=` pointing here would loop). */
const AUTH_PATHS = [
  "/sign-in",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/accept-invite",
  "/email/unsubscribe",
  "/enrol-totp",
]

/**
 * Where `?from=` may send a user after sign-in: a path on this site only.
 *
 * Resolved the way the browser will resolve it, with `new URL(from, origin)`,
 * so the parser's own rules (tabs and newlines dropped, `\` read as `/`, dot
 * segments collapsed) cannot turn an innocent-looking string into
 * `//evil.example`. The result must be same-origin; what is returned is its
 * normalised `pathname + search + hash`, never the raw input. An auth screen
 * is the overview, so a crafted link cannot bounce a fresh session elsewhere
 * or into a loop.
 */
export function safeFrom(from: string | null | undefined): string {
  if (!from?.startsWith("/")) return "/"
  const origin = window.location.origin
  let url: URL
  try {
    url = new URL(from, origin)
  } catch {
    return "/"
  }
  if (url.origin !== origin) return "/"
  // URL keeps empty segments: "/.//evil" resolves to the pathname "//evil",
  // which a later navigation would read as a protocol-relative URL.
  if (url.pathname.startsWith("//")) return "/"
  if (AUTH_PATHS.some((p) => url.pathname === p || url.pathname.startsWith(`${p}/`))) return "/"
  return url.pathname + url.search + url.hash
}

/** `/enrol-totp`, keeping where the user was going, for a destination after enrolment. */
export function enrolPath(from: string | null | undefined): string {
  const next = safeFrom(from)
  return next === "/" ? "/enrol-totp" : `/enrol-totp?from=${encodeURIComponent(next)}`
}
